import React, { useCallback, useEffect, useState } from 'react'
import { BuilderComponent, builder } from '@builder.io/react'
import Modal from 'client/components/Modal'
import { VARIANT_MID_SCREEN } from 'client/components/Modal/constants'
import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import { SUBSCRIPTION_MODAL_HOME_PAGE } from 'src/constants/subscriptionSources'
import { subscribeRequestAction } from 'src/effects/SubscriptionStatus/actions'
import { UNSUBSCRIBED } from 'src/effects/SubscriptionStatus/constants'
import { getSubscriptionStatus } from 'src/effects/SubscriptionStatus/selectors'
import { useMarket } from 'src/hooks/useMarket'
import { SubscriptionModal } from '../Molecules/SubscriptionModal'
import { getSubscriptionModalClosedCookie, setSubscriptionModalClosedCookie } from './utils'
import { FormattedMessage } from 'react-intl'
import { useBusiness } from 'src/hooks/useBusiness'
import { AFFILIATES_EMAIL } from 'src/constants/affiliates'

interface IBuilderModalProps {
  isSubscriptionModalActive?: boolean
}

const BuilderModal = ({ isSubscriptionModalActive = false }: IBuilderModalProps) => {
  const { locale, market, language, feedbackMessageType } = useMarket()
  const { affiliate } = useBusiness()
  const [showBuilderModal, setShowBuilderModal] = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [content, setContent] = useState()
  const allMarkets = 'all'
  const modalId = 'en-modal'

  const dispatch = useDispatch()

  const subscriptionStatus = useSelector(getSubscriptionStatus)

  const handleSubscribe = (emailToSubscribe: string) => {
    setSubscriptionModalClosedCookie()
    dispatch(
      subscribeRequestAction({
        hash: emailToSubscribe,
        language,
        market,
        feedbackMessageType,
        source: SUBSCRIPTION_MODAL_HOME_PAGE,
      })
    )
  }

  const handleCloseSubscriptionModal = () => {
    setShowSubscriptionModal(false)
    setSubscriptionModalClosedCookie()
  }

  const isAffiliateEmail = affiliate && AFFILIATES_EMAIL.includes(affiliate)

  const router = useRouter()
  const { builder_modal: builderModal } = router.query

  const getModal = useCallback(
    (marketToSearch: string) => {
      return builder
        .get(modalId, {
          url: window.location.pathname,
          userAttributes: { locale },
          includeUnpublished: builderModal === 'draft',
          options: {
            locale,
          },
          query: {
            'data.markets': {
              $elemMatch: {
                market: { $regex: `^${marketToSearch}$`, $options: 'i' },
              },
            },
          },
        })
        .promise()
    },
    [locale, builderModal]
  )

  useEffect(() => {
    getModal(market)
      .then(res => {
        if (res) {
          return res
        }
        return getModal(allMarkets)
      })
      .then(res => {
        setContent(res)
        setShowBuilderModal(!!res)
      })

    if (
      isSubscriptionModalActive &&
      !isAffiliateEmail &&
      !showBuilderModal &&
      !getSubscriptionModalClosedCookie()
    ) {
      setShowSubscriptionModal(true)
    }
  }, [
    getModal,
    market,
    showBuilderModal,
    subscriptionStatus,
    isSubscriptionModalActive,
    isAffiliateEmail,
  ])

  if (!showBuilderModal && !showSubscriptionModal) {
    return null
  }

  if (showSubscriptionModal && subscriptionStatus === UNSUBSCRIBED) {
    return (
      <Modal
        show={showSubscriptionModal}
        handleClose={handleCloseSubscriptionModal}
        maxWidth="430px"
        displayHeader={false}
        title={<FormattedMessage id="global.modal-home-subscription-header" />}
      >
        <SubscriptionModal status={subscriptionStatus} onSubscribe={handleSubscribe} />
      </Modal>
    )
  }

  return (
    <Modal
      show={showBuilderModal}
      handleClose={() => setShowBuilderModal(false)}
      variant={VARIANT_MID_SCREEN}
    >
      <BuilderComponent model={modalId} content={content} locale={locale} />
    </Modal>
  )
}

export default BuilderModal
